import React, { useEffect, useState } from 'react';

import { Tabs as MantineTabs, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import ShoppingCart from 'components/ShoppingCart';
import ShoppingCartWithBadge from 'components/ShoppingCartWithBadge';
import { useAuth } from 'context/auth-context';
import DrawerFeesAndMiscProductsForm from 'pages/private/owner/FeesAndMiscProducts/components/DrawerFeesAndMiscProductsForm';
import FeesAndMiscProductsTable from 'pages/private/owner/FeesAndMiscProducts/components/FeesAndMiscProductsTable';
import DrawerRetailForm from 'pages/private/owner/Retail/components/DrawerRetailForm';
import RetailTable from 'pages/private/owner/Retail/components/RetailTable';
import { useLocation } from 'react-router-dom';
import { ProductRetrieve } from 'types';

import PackageList from './components/PackageList';
import ProductsHeader from './components/ProductsHeader';

const Products = (): JSX.Element => {
  const theme = useMantineTheme();
  const location = useLocation();
  const { hasPerm } = useAuth();
  const productMutatePerm = hasPerm('ProductMutate');
  const invoiceCreatePerm = hasPerm('InvoiceCreate');

  const [searchRetail, setSearchRetail] = useState<string>('');
  const [searchFeesMisc, setSearchFeesMisc] = useState<string>('');
  const [searchPackages, setSearchPackages] = useState<string>('');
  const [orderByFavoritesFeesMisc, setOrderByFavoritesFeesMisc] = useState<boolean>(true);
  const [orderByFavoritesRetail, setOrderByFavoritesRetail] = useState<boolean>(true);
  const [debouncedSearchRetail] = useDebouncedValue<string>(searchRetail, 250);
  const [debouncedSearchFeesMisc] = useDebouncedValue<string>(searchFeesMisc, 250);
  const [debouncedSearchPackages] = useDebouncedValue<string>(searchPackages, 250);

  const [isFeeAndMiscDrawerOpened, { toggle: toggleDrawer }] = useDisclosure(false);
  const [isRetailDrawerOpened, { toggle: handleRetailDrawerToggle }] = useDisclosure(false);
  const [productId, setProductId] = useState<string>('');
  const [retailId, setRetailId] = useState<string>('');

  const [cartItems, setCartItems] = useState<ProductRetrieve[]>([]);
  const [isShoppingCartOpened, { toggle: handleShoppingCartToggle }] = useDisclosure(false);

  const handleCloseDrawer = (): void => {
    setProductId('');
    toggleDrawer();
  };
  const [activeTab, setActiveTab] = useState<string | null>('retail');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['retail', 'fees-and-misc', 'packages'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  const tabs = [
    {
      label: 'Retail',
      value: 'retail',
      component: (
        <RetailTable
          search={debouncedSearchRetail}
          orderByFavorites={orderByFavoritesRetail}
          openDrawer={handleRetailDrawerToggle}
          setRetailId={setRetailId}
          cartItems={cartItems}
          setCartItems={setCartItems}
          productMutatePerm={productMutatePerm}
          invoiceCreatePerm={invoiceCreatePerm}
          bgc='gray'
        />
      ),
    },
    {
      label: 'Fees and Miscellaneous',
      value: 'fees-and-misc',
      component: (
        <FeesAndMiscProductsTable
          search={debouncedSearchFeesMisc}
          orderByFavorites={orderByFavoritesFeesMisc}
          setProductId={setProductId}
          openDrawer={toggleDrawer}
          cartItems={cartItems}
          setCartItems={setCartItems}
          productMutatePerm={productMutatePerm}
          invoiceCreatePerm={invoiceCreatePerm}
          bgc='gray'
        />
      ),
    },
    {
      label: 'Packages',
      value: 'packages',
      component: <PackageList search={debouncedSearchPackages} />,
    },
  ];

  return (
    <>
      {!isShoppingCartOpened ? (
        <MantineTabs
          className='content-no-padding'
          value={activeTab}
          onTabChange={setActiveTab}
          sx={{
            backgroundColor: theme.colors.gray[0],
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem', paddingBottom: 0 }}
          >
            <MantineTabs.List>
              {tabs.map(item => (
                <MantineTabs.Tab
                  key={item.value}
                  value={item.value}
                  sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                >
                  {item.label}
                </MantineTabs.Tab>
              ))}
            </MantineTabs.List>
            {activeTab !== 'packages' && (
              <ShoppingCartWithBadge onClick={handleShoppingCartToggle} count={cartItems.length} />
            )}
          </div>
          <ProductsHeader
            search={
              activeTab === 'packages'
                ? searchPackages
                : activeTab === 'retail'
                ? searchRetail
                : searchFeesMisc
            }
            setSearch={
              activeTab === 'packages'
                ? setSearchPackages
                : activeTab === 'retail'
                ? setSearchRetail
                : setSearchFeesMisc
            }
            orderByFavorites={
              activeTab === 'packages'
                ? false
                : activeTab === 'retail'
                ? orderByFavoritesRetail
                : orderByFavoritesFeesMisc
            }
            setOrderByFavorites={
              activeTab === 'packages'
                ? () => {}
                : activeTab === 'retail'
                ? setOrderByFavoritesRetail
                : setOrderByFavoritesFeesMisc
            }
            hideOrderByFavorites={activeTab === 'packages'}
          />
          {tabs.map(item => (
            <MantineTabs.Panel
              key={item.value}
              value={item.value}
              className={item.value === activeTab ? 'content-no-padding' : undefined}
            >
              {item.component}
            </MantineTabs.Panel>
          ))}
        </MantineTabs>
      ) : (
        <div className='content-no-padding'>
          <ShoppingCart
            isDesktop={false}
            visible={isShoppingCartOpened}
            cartItems={cartItems}
            setCartItems={setCartItems}
            closeCart={handleShoppingCartToggle}
            permission={invoiceCreatePerm}
            productTypeModule={activeTab || ''}
          />
        </div>
      )}
      <DrawerFeesAndMiscProductsForm
        opened={isFeeAndMiscDrawerOpened}
        closeHandler={handleCloseDrawer}
        productId={productId}
        permission={productMutatePerm}
        handleResetCart={() => setCartItems([])}
      />
      <DrawerRetailForm
        opened={isRetailDrawerOpened}
        closeHandler={handleRetailDrawerToggle}
        productId={retailId}
        permission={productMutatePerm}
        handleResetCart={() => setCartItems([])}
      />
    </>
  );
};

export default Products;
